// Build a Date from a wall-clock date + time in America/Los_Angeles, honoring DST.
// (A hard-coded "-07:00" is only right in summer; in winter it shifts times by an hour.)
const LA = 'America/Los_Angeles'
const fmt = new Intl.DateTimeFormat('en-US', {
  timeZone: LA, hourCycle: 'h23',
  year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
})

function laOffsetMs(utcMs) {
  const p = {}
  fmt.formatToParts(new Date(utcMs)).forEach(x => { p[x.type] = x.value })
  const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second)
  return asUtc - Math.floor(utcMs / 1000) * 1000
}

// laDateTime('2026-11-27', '13:00') → the instant when it's 1:00 PM in Los Angeles.
export function laDateTime(dateStr, timeStr = '12:00') {
  const [y, m, d] = String(dateStr).split('-').map(Number)
  const [hh, mm] = String(timeStr || '12:00').split(':').map(Number)
  const guess = Date.UTC(y, m - 1, d, hh || 0, mm || 0, 0)
  let utc = guess - laOffsetMs(guess)
  utc = guess - laOffsetMs(utc) // re-check across a DST boundary
  return new Date(utc)
}

// A Date whose LOCAL fields equal the Los Angeles wall-clock for the given instant, so that
// date-fns format(laWall(ts), 'h:mm a') prints Pacific time no matter where the viewer is.
export function laWall(value) {
  // A string with no zone designator ('2026-10-15', '2026-10-15T00:00:00') is already a
  // calendar/wall-clock value — don't shift it; just read it as local fields.
  if (typeof value === 'string' && !/(Z|[+-]\d{2}:?\d{2})$/i.test(value.trim()) && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/)
    return new Date(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0))
  }
  const d = value instanceof Date ? value : new Date(value)
  const p = {}
  fmt.formatToParts(d).forEach(x => { p[x.type] = x.value })
  return new Date(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second)
}

// Today's date (YYYY-MM-DD) in Los Angeles — NOT the browser's day, and not UTC.
// Use for every "default to today" field so a viewer in Texas (or anywhere) gets the company's date.
export function laToday() {
  const p = {}
  fmt.formatToParts(new Date()).forEach(x => { p[x.type] = x.value })
  return `${p.year}-${p.month}-${p.day}`
}

// "Now" as a Date whose local fields are the current Los Angeles wall-clock.
export function laNow() { return laWall(new Date()) }

// Timestamp → zone-less Los Angeles wall-clock string ('2026-10-15T20:30:00').
// Safe to feed to new Date()/laWall() later: it is read back as local fields, never shifted.
export function laWallString(value) {
  if (!value) return value
  const w = laWall(value)
  const p = n => String(n).padStart(2, '0')
  return `${w.getFullYear()}-${p(w.getMonth() + 1)}-${p(w.getDate())}T${p(w.getHours())}:${p(w.getMinutes())}:${p(w.getSeconds())}`
}

// The Los Angeles calendar date (YYYY-MM-DD) of a timestamp.
export function laDateStr(value) {
  return value ? laWallString(value).slice(0, 10) : ''
}

// Billable rental days: whole 24-hour blocks of LOS WALL-CLOCK time, any partial block = 1 day.
// Measuring wall-clock (not elapsed ms) keeps a rental across a DST change honest: pickup 8:30 AM
// Oct 31 → return 8:30 AM Nov 1 is 25 real hours but still exactly 1 day. Returns 0 if end <= start.
// Accepts Dates / timestamps, or zone-less 'YYYY-MM-DDTHH:mm' strings (read as LA wall-clock).
function laWallMs(value) {
  if (typeof value === 'string' && !/(Z|[+-]\d{2}:?\d{2})$/i.test(value.trim()) && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/)
    return Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0))
  }
  const d = value instanceof Date ? value : new Date(value)
  if (isNaN(d.getTime())) return NaN
  const p = {}
  fmt.formatToParts(d).forEach(x => { p[x.type] = x.value })
  return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second)
}

export function rentalDays(start, end) {
  if (!start || !end) return 0
  const ms = laWallMs(end) - laWallMs(start)
  return ms > 0 ? Math.ceil(ms / 86400000) : 0
}
